import {
  HsClippingPlane,
  HsClippingStorage,
  hsApplyVisualQualityToShader,
  type HsShaderData,
  type HsVisualQualitySettings,
} from '@sibe/hyperscene';
import type { HsSceneLook } from './scene-source.class';

/** A clipping plane `normal · p + constant = 0`; points on the positive side are cut away. */
export interface HsSceneClippingPlane {
  normal: readonly [number, number, number];
  constant: number;
}

export interface HsSceneLookSettings {
  /** Scene opacity, 0..1 (default 1). */
  opacity?: number;
  /** Draw every material opaque; opacity is then ignored (default false). */
  disableTransparentMaterials?: boolean;
  clippingPlanes?: readonly HsSceneClippingPlane[];
  /**
   * Storage to write `clippingPlanes` into instead of a new one, when it holds as many planes: shaders read the
   * planes from their storage on every draw, so a look rebuilt for moved planes keeps the drawn shaders and only
   * their uniforms change. Pass the previous look's `clippingStorage`.
   */
  clippingStorage?: HsClippingStorage | null;
  /** Color points of point clouds that collide with meshes (default false). */
  highlightCollisions?: boolean;
  /** Share geometry between instances and batch them (default true). */
  instancing?: boolean;
  /** Point cloud point size in pixels (default 2). */
  pointSize?: number;
  /** Lighting and tone; without it scene shaders stay unchanged. */
  quality?: HsVisualQualitySettings;
  /** Called whenever a scene shader is built, for diagnostics. */
  onShaderBuilt?(): void;
}

/**
 * The look of a scene for `HsSceneSource`, with a signature that changes exactly when its shaders do. Clipping plane
 * positions are uniforms, so only the number of planes is part of the signature: moving a section keeps the drawn
 * objects and their shaders.
 */
export function hsSceneLook(settings: HsSceneLookSettings = {}): HsSceneLook {
  const disableTransparentMaterials: boolean = settings.disableTransparentMaterials ?? false;
  const opacity: number = disableTransparentMaterials ? 1 : settings.opacity ?? 1;
  const planes: readonly HsSceneClippingPlane[] = settings.clippingPlanes ?? [];
  const quality: HsVisualQualitySettings | undefined = settings.quality;
  let clippingStorage: HsClippingStorage | null = null;
  if (planes.length > 0) {
    const reused: HsClippingStorage | null = settings.clippingStorage ?? null;
    if (reused !== null && hsWriteClippingPlanes(reused, planes)) {
      clippingStorage = reused;
    } else {
      clippingStorage = new HsClippingStorage(planes.map(() => new HsClippingPlane(0, 0, 0, 0)));
      hsWriteClippingPlanes(clippingStorage, planes);
    }
  }
  const instancing: boolean = settings.instancing ?? true;
  return {
    signature: [
      `opacity:${opacity}`,
      `disableTransparentMaterials:${disableTransparentMaterials}`,
      `clip:${planes.length}`,
      `collision:${settings.highlightCollisions ?? false}`,
      `quality:${quality === undefined ? 'none' : hsVisualQualitySignature(quality)}`,
    ].join('|'),
    opacity,
    disableTransparentMaterials,
    clippingStorage,
    highlightCollisions: settings.highlightCollisions ?? false,
    instancing,
    instancingGeometry: instancing,
    pointSize: settings.pointSize ?? 2,
    configureMeshShader: (shaderData: HsShaderData): HsShaderData => {
      settings.onShaderBuilt?.();
      return quality === undefined ? shaderData : hsApplyVisualQualityToShader(shaderData, quality);
    },
  };
}

/**
 * Writes planes into a clipping storage that holds as many, in the shaders' convention; returns false and leaves the
 * storage alone when the counts differ, since the shaders' plane count is compiled in.
 */
export function hsWriteClippingPlanes(storage: HsClippingStorage, planes: readonly HsSceneClippingPlane[]): boolean {
  if (storage.clippingPlanes.length !== planes.length) {
    return false;
  }
  planes.forEach((plane: HsSceneClippingPlane, index: number) => {
    // Shaders discard where dot(v_position, n) > offset, and v_position is the negated world position, so the
    // plane is stored negated: dot(-p, -normal) > -constant cuts exactly where normal · p + constant > 0.
    const stored: HsClippingPlane = storage.clippingPlanes[index];
    stored.normal.set(-plane.normal[0], -plane.normal[1], -plane.normal[2]);
    stored.offset = -plane.constant;
  });
  return true;
}

/** The shader-relevant part of visual quality settings; equal strings build equal shaders. */
export function hsVisualQualitySignature(settings: HsVisualQualitySettings): string {
  return [
    settings.shadingMode,
    settings.ambientIntensity,
    settings.directionalIntensity,
    settings.specularEnabled,
    settings.specularIntensity,
    settings.fresnelEnabled,
    settings.fresnelIntensity,
    settings.exposure,
    settings.contrast,
  ].join('|');
}
